import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UnderlingUser } from '../../models/SystemTables';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

interface UnderlingUsersSideMenuProps {
  underlingUsers: UnderlingUser[];
  selectedUnderlingUser: UnderlingUser | null; // Which user is currently active
  onUserClick: (user: UnderlingUser) => void; // Callback when a user is clicked
}

const UnderlingUsersSideMenu: React.FC<UnderlingUsersSideMenuProps> = ({
  underlingUsers,
  selectedUnderlingUser,
  onUserClick,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleNewOrderClick = () => {
    navigate('/new-order');
  };

  return (
    <Paper
      sx={{
        backgroundColor: 'var(--header-bg)',
        padding: '1rem',
        borderRadius: '4px',
        flexShrink: 1,
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        border: '1px solid #ddd',
        minWidth: '8rem',
      }}
    >
      <Button
        onClick={handleNewOrderClick}
        fullWidth
        sx={{
          mb: '0.5rem',
          padding: '0.5rem',
          backgroundColor: 'var(--green-menu-button-bg)',
          color: 'var(--green-menu-button-text)',
          boxShadow: '0 2px 4px var(--green-menu-button-shadow)',
          textTransform: 'none',
          '&:hover': {
            backgroundColor: 'var(--green-menu-button-bg-pressed)',
          },
        }}
      >
        {t('UnderlingUsersSideMenu.NewOrder')}
      </Button>
      <Typography
        variant="h6"
        align="center"
        sx={{
          mb: '2rem',
          color: 'var(--header-text)',
        }}
      >
        {t('UnderlingUsersSideMenu.Title')}
      </Typography>
      <Box>
        {underlingUsers.map((user) => {
          const isActive =
            selectedUnderlingUser?.attributes.sub === user.attributes.sub;
          return (
            <Button
              key={user.attributes.sub}
              onClick={() => onUserClick(user)}
              fullWidth
              sx={{
                mb: '0.5rem',
                padding: '0.5rem',
                backgroundColor: isActive
                  ? 'var(--green-menu-button-bg-pressed)'
                  : 'var(--green-menu-button-bg)',
                color: 'var(--green-menu-button-text)',
                boxShadow: isActive
                  ? 'none'
                  : '0 2px 4px var(--green-menu-button-shadow)',
                fontWeight: isActive ? 'bold' : 'normal',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'var(--green-menu-button-bg-pressed)',
                },
              }}
            >
              {user.username}
            </Button>
          );
        })}
      </Box>
    </Paper>
  );
};

export default UnderlingUsersSideMenu;
